import {
  ArrowLeftRight,
  Boxes,
  Building2,
  Calculator,
  CalendarDays,
  ChartColumn,
  ClipboardCheck,
  ClipboardList,
  Code,
  Cog,
  Contact,
  DollarSign,
  Factory,
  Handshake,
  Landmark,
  LayoutList,
  Link2,
  ListTree,
  Network,
  Package,
  PackageCheck,
  Palette,
  ReceiptText,
  ScrollText,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Tag,
  Tags,
  Ticket,
  Undo2,
  Users,
  Warehouse,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

import {
  type AccessRoute,
  ROUTE_ACCESS,
  routeRequirements,
  routeRequiresBrandMembership,
} from '../lib/access/index.js';

export type AppNavItem = {
  label: string;
  icon: LucideIcon;
  required: readonly string[];
  singular?: string;
  requiresBrandMembership?: boolean;
  tailCrumbLabels?: Readonly<Record<string, string>>;
} & ({ to: string; href?: never } | { href: string; to?: never });

export type AppNavSection = {
  id: string;
  title: string;
  items: AppNavItem[];
};

type NavItemOptions = {
  icon: LucideIcon;
  singular?: string;
  tailCrumbLabels?: Readonly<Record<string, string>>;
  /** Served outside the SPA router, so the sidebar renders a plain link. */
  external?: boolean;
};

/** Label and permissions come from `ROUTE_ACCESS`, the list the router and denied page read too. */
function navItem(route: AccessRoute, { external, ...options }: NavItemOptions): AppNavItem {
  const access = {
    label: ROUTE_ACCESS[route].label,
    required: routeRequirements(route),
    ...(routeRequiresBrandMembership(route) && { requiresBrandMembership: true }),
    ...options,
  };
  return external ? { ...access, href: route } : { ...access, to: route };
}

export const APP_NAV: AppNavSection[] = [
  {
    id: 'foundations',
    title: 'Foundations',
    items: [
      navItem('/company-setup', { icon: Building2 }),
      navItem('/company-settings', { icon: Settings }),
      navItem('/departments', { icon: Network, singular: 'Department' }),
      navItem('/roles', { icon: ShieldCheck, singular: 'Role' }),
      navItem('/users', { icon: Users }),
      navItem('/parts', { icon: Cog, singular: 'Part' }),
      navItem('/warehouses', { icon: Warehouse, singular: 'Warehouse' }),
      navItem('/customers', { icon: Contact, singular: 'Customer' }),
      navItem('/vendors', { icon: Handshake, singular: 'Vendor' }),
      navItem('/brands', { icon: Tag, singular: 'Brand' }),
      navItem('/products', { icon: Package, singular: 'Product' }),
      navItem('/product-categories', { icon: LayoutList, singular: 'Product Category' }),
    ],
  },
  {
    id: 'sales',
    title: 'Sales',
    items: [
      navItem('/sales-orders', { icon: ShoppingBag, singular: 'Sales Order' }),
      navItem('/accounts-receivable', { icon: Landmark }),
      navItem('/credits', { icon: Ticket }),
      navItem('/pricing', { icon: DollarSign }),
      navItem('/pricing-rules', { icon: Tags, singular: 'Pricing Rule' }),
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    items: [navItem('/invoices', { icon: ReceiptText, singular: 'Invoice' })],
  },
  {
    id: 'events',
    title: 'Events',
    items: [
      navItem('/events', {
        icon: CalendarDays,
        singular: 'Event',
        tailCrumbLabels: { 'templates/new': 'Template' },
      }),
    ],
  },
  {
    id: 'sourcing',
    title: 'Sourcing',
    items: [
      navItem('/purchase-orders', { icon: ShoppingCart, singular: 'Purchase Order' }),
      navItem('/supplier-returns', { icon: Undo2 }),
    ],
  },
  {
    id: 'operations',
    title: 'Operations',
    items: [
      navItem('/receiving', { icon: PackageCheck }),
      navItem('/picking', { icon: ClipboardCheck }),
      navItem('/inventory', { icon: Boxes }),
      navItem('/transfer-orders', { icon: ArrowLeftRight, singular: 'Transfer Order' }),
      navItem('/manufacturing', { icon: Factory, singular: 'Manufacturing Order' }),
      navItem('/manufacturing/work-orders', { icon: ClipboardList }),
      navItem('/boms', { icon: ListTree, singular: 'BOM' }),
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    items: [
      navItem('/integrations/leaflink', { icon: Link2 }),
      navItem('/integrations/sage-intacct', { icon: Calculator }),
    ],
  },
  {
    id: 'system',
    title: 'System',
    items: [
      navItem('/audit-trail', { icon: ScrollText }),
      navItem('/custom-reports', { icon: ChartColumn }),
    ],
  },
  {
    id: 'developer',
    title: 'Developer',
    items: [
      navItem('/design-system', { icon: Palette }),
      navItem('/api', { icon: Code, external: true }),
      navItem('/workflows', { icon: Workflow, external: true }),
    ],
  },
];
